import { RoleName, PlatformRole, PlatformFeature, codeToRole, roleToCode } from './roles';
import { MembershipStatus, MembershipType } from './membership';

/**
 * `no`   — no product access beyond whatever public content requires no auth.
 * `free` — has access ONLY because `CapicheSubscriptionService.FREE_MODE` is
 *          globally on (CSW-1660, 2026-08-22). Structural/temporary — once
 *          the real pay-gate goes live, a `free` account must actually
 *          subscribe (-> `yes`) or lose access (-> `no`). This is the
 *          default for a normal new signup today, and is what closes the
 *          gap `no` used to leave: recording `no` for someone who currently
 *          DOES have full access (via FREE_MODE) was recording something
 *          false.
 * `trial` — an explicit, revocable PER-USER grant, distinct from `free` —
 *           used for a denied/removed teacher so they aren't stuck with zero
 *           access, without leaning on the blanket FREE_MODE flag (fragile —
 *           see CSW-1637 "Why b2cAccess is three-valued, not a bool").
 *           Deliberately meant to SURVIVE the pay-gate going live — unlike
 *           `free`, a `trial` grant is a real, targeted decision that stays
 *           in force after FREE_MODE turns off, not a side-effect of it
 *           being on. Who revokes `trial` back to `no`, and whether it
 *           expires, is a deliberately deferred open question — revisit only
 *           when the pay-gate is actually turned on.
 * `yes`  — a real, paid subscription.
 */
export type B2cAccess = 'no' | 'free' | 'trial' | 'yes';

/**
 * ID token custom claims (Firebase caps these at 1000 bytes). Rebuilt from
 * Firestore (`userRights/{uid}`) on every relevant transition — this is a
 * lean, disposable cache, NOT the source of truth. See claims.ts vs
 * membership.ts split in CSW-1637 ("Two distinct shapes, not one").
 */
/** One org's entry in the claim's `memberships` map. `type` is carried so a
 *  routing guard can tell a plain teacher from a student — both have
 *  `roles: []` (roles are elevated grants only). `roles` are short codes
 *  (see roles.ts); `classes` never enters the claim.
 *
 *  `status` is carried so a routing guard can send a PENDING teacher to the
 *  teacher portal's awaiting-approval screen (and a DENIED one to its denied
 *  screen), not just active members — the legacy `role: 'teacher'` claim did
 *  this implicitly by always being present. `removed` never reaches the
 *  claim. */
export interface ClaimMembership {
  type: MembershipType;
  roles: string[];
  status: Exclude<MembershipStatus, 'removed'>;
}

/** Personal (non-org) product-access section of the claim — mirrors the
 *  `access` field of `userRights/{uid}.b2c`. A wrapper object, not a bare
 *  `b2cAccess` scalar at the claim root, so it lines up 1:1 with the
 *  Firestore doc and has room to grow (e.g. a trial expiry) without adding
 *  another root claim key. */
export interface ClaimB2c {
  access: B2cAccess;
}

/** Capiche-level section of the claim — carried verbatim from
 *  `userRights/{uid}.platform` (same PLATFORM_ROLES / PLATFORM_FEATURES
 *  vocabulary). Read with `claimHasPlatformRole` / `claimHasPlatformFeature`
 *  so the checked string is compile-verified against the vocabulary. */
export interface ClaimPlatform {
  roles: PlatformRole[];
  features: PlatformFeature[];
}

/**
 * The new-model ID-token custom claims (CSW-1662). Deliberately mirrors the
 * three sections of `userRights/{uid}` — `memberships` / `b2c` / `platform`
 * — so every field is (a) unmistakably distinct from the legacy flat claims
 * (`role` / `orgId` / `orgAdmin` / `classId` / `capicheAdmin` /
 * `extensionBeta`), which the additive merge leaves untouched, and (b) read
 * with the same predicate a server-side `userRights` check uses. The
 * `userRightsToClaims` projection just drops what a token doesn't need
 * (Stripe ids off `b2c`, `classes[]` off memberships) and caps the
 * membership count.
 */
export interface CapicheClaims {
  /** orgId -> { type, role codes, status }. Carries active + pending + denied;
   *  never `removed`. Empty (with `overflow` set) once the count exceeds
   *  CLAIMS_OVERFLOW_MEMBERSHIP_THRESHOLD. */
  memberships: Record<string, ClaimMembership>;
  /** Always present — `userRightsToClaims` is a total projection and defaults
   *  a missing `userRights.b2c` section to `{ access: 'free' }`. */
  b2c: ClaimB2c;
  /** Absent for a normal user (both lists empty); present verbatim otherwise. */
  platform?: ClaimPlatform;
  /** Set once the carried-membership count exceeds CLAIMS_OVERFLOW_MEMBERSHIP_THRESHOLD.
   *  When true, `memberships` is empty and the client must read Firestore directly
   *  instead of trusting the claim for org membership. */
  overflow?: true;
}

/**
 * Safety-net threshold, not a target: recomputed for the readable-role-code +
 * full-orgId encoding (see CSW-1637 "Why the encoding changed"), a token can
 * hold role/org data for roughly this many concurrent active memberships
 * before risking the 1000-byte claims cap. No real user is anywhere close —
 * 2-5 memberships is typical even for a peripatetic teacher.
 */
export const CLAIMS_OVERFLOW_MEMBERSHIP_THRESHOLD = 26;

/** The minimal shape buildClaims() needs from a membership — callers pass
 *  their own MembershipRecord (or a DB row projected to this) directly. */
export interface MembershipForClaims {
  orgId: string;
  type: MembershipType;
  status: MembershipStatus;
  roles: RoleName[];
}

/**
 * Builds the ID-token custom claims object from a user's full set of
 * memberships plus their b2c access and platform section.
 *
 * Carries active + pending + denied memberships (each with its `status`) so a
 * routing guard can place a pending/denied teacher, not only an active one.
 * `removed` is filtered out here so no caller can leak it into the claim.
 * `platform` is omitted entirely when both its lists are empty (mirrors
 * `userRights` — absent = a normal user).
 */
export function buildClaims(params: {
  b2cAccess: B2cAccess;
  platform?: { roles: PlatformRole[]; features: PlatformFeature[] };
  memberships: MembershipForClaims[];
}): CapicheClaims {
  const carried = params.memberships.filter(
    (m): m is MembershipForClaims & { status: ClaimMembership['status'] } => m.status !== 'removed',
  );

  const b2c: ClaimB2c = { access: params.b2cAccess };
  const platform: ClaimPlatform | undefined =
    params.platform && (params.platform.roles.length || params.platform.features.length)
      ? { roles: [...params.platform.roles], features: [...params.platform.features] }
      : undefined;

  if (carried.length > CLAIMS_OVERFLOW_MEMBERSHIP_THRESHOLD) {
    return { memberships: {}, b2c, ...(platform ? { platform } : {}), overflow: true };
  }

  const memberships: Record<string, ClaimMembership> = {};
  for (const m of carried) {
    memberships[m.orgId] = { type: m.type, roles: m.roles.map(roleToCode), status: m.status };
  }

  return { memberships, b2c, ...(platform ? { platform } : {}) };
}

/**
 * Decodes the role codes for one org out of a claim back into readable role
 * names. Unrecognised codes are dropped, not thrown — see codeToRole().
 */
export function decodeClaimRoles(codes: string[] | undefined): RoleName[] {
  if (!codes) return [];
  return codes.map(codeToRole).filter((r): r is RoleName => r !== undefined);
}

/** True if the claim's `memberships` map cannot be trusted and Firestore must be
 *  read directly instead. */
export function isOverflowed(claims: Pick<CapicheClaims, 'overflow'>): boolean {
  return claims.overflow === true;
}

/**
 * Pulls the new-model claims section out of a raw token bag — a backend
 * `DecodedIdToken` or a frontend `IdTokenResult.claims`. The four new-model
 * keys (`memberships` / `b2c` / `platform` / `overflow`) sit at the token
 * root alongside the legacy flat claims, because `restampCapicheClaims`
 * merges them in additively (CSW-1662). A token that predates the stamp has
 * none of them: `memberships` defaults to `{}`, `b2c` to `{ access: 'free' }`
 * (matches `userRightsToClaims`' total-projection default), `platform` stays
 * undefined (⇒ `claimHasPlatformRole` is false), `overflow` false.
 */
export function capicheClaimsFromToken(
  token: Record<string, unknown> | null | undefined,
): CapicheClaims {
  const t = token ?? {};
  const memberships = (t['memberships'] as CapicheClaims['memberships'] | undefined) ?? {};
  const b2c = (t['b2c'] as ClaimB2c | undefined) ?? { access: 'free' };
  const platform = (t['platform'] as ClaimPlatform | null | undefined) || undefined;
  const overflow = t['overflow'] === true ? (true as const) : undefined;
  return {
    memberships,
    b2c,
    ...(platform ? { platform } : {}),
    ...(overflow ? { overflow } : {}),
  };
}

/** Convenience: does this claim carry an active membership with the given
 *  role at the given org? Returns false (not throws) on an overflowed claim
 *  — callers must fall back to Firestore themselves when isOverflowed(). */
export function claimHasRole(claims: CapicheClaims, orgId: string, role: RoleName): boolean {
  if (claims.overflow) return false;
  const entry = claims.memberships[orgId];
  if (!entry || entry.status !== 'active') return false;
  return decodeClaimRoles(entry.roles).includes(role);
}

/** The base membership type for one org, or undefined if there's no active
 *  membership there (or the claim overflowed). This is what a portal-routing
 *  guard reads. */
export function claimMembershipType(claims: CapicheClaims, orgId: string): MembershipType | undefined {
  if (claims.overflow) return undefined;
  return claims.memberships[orgId]?.type;
}

/** True if the claim carries any staff-side membership (teacher or
 *  non-teaching staff) in ANY status — active, pending or denied. This is the
 *  "belongs in / gets routed to the teacher portal" check: it deliberately
 *  matches the legacy `role: 'teacher'` identity marker, which was kept in
 *  place through pending/denied/removal (CSW-1651) precisely so the portal
 *  keeps showing the right screen. `removed` never reaches the claim. */
export function claimHasAnyStaffMembership(claims: CapicheClaims): boolean {
  if (claims.overflow) return false;
  return Object.values(claims.memberships).some((m) => m.type === 'teacher' || m.type === 'staff');
}

/** True if the claim carries an ACTIVE staff-side membership at any org — an
 *  approved-and-set-up teacher/staff member. Replaces the legacy
 *  `role === 'teacher' && orgId` pair: no active staff membership ⇒ still
 *  pending/denied ⇒ the portal sends them to /setup. */
export function claimHasActiveStaffMembership(claims: CapicheClaims): boolean {
  if (claims.overflow) return false;
  return Object.values(claims.memberships).some(
    (m) => (m.type === 'teacher' || m.type === 'staff') && m.status === 'active',
  );
}

/** True if the claim carries any student membership (any status). Replaces a
 *  legacy `role === 'student'` check in the post-login role router. */
export function claimHasAnyStudentMembership(claims: CapicheClaims): boolean {
  if (claims.overflow) return false;
  return Object.values(claims.memberships).some((m) => m.type === 'student');
}

/** True if the claim carries ANY org membership at all — of any type
 *  (including `unassigned`) and any status except `removed` (which never
 *  reaches the claim). This is the "belongs on an org-facing screen" check:
 *  an `unassigned`/pending person has claimed an affiliation but has no
 *  staff or student membership yet, so the more specific predicates all
 *  return false — this one routes them to the awaiting-approval screen
 *  instead of the consumer app. */
export function claimHasAnyMembership(claims: CapicheClaims): boolean {
  if (claims.overflow) return false;
  return Object.keys(claims.memberships).length > 0;
}

/** The orgId of this user's first ACTIVE staff membership, or undefined.
 *  Replaces the legacy `orgId` claim as the "which school is this teacher
 *  acting in" source for `/teacher/*` handlers. Single-org today; a
 *  multi-org teacher's org selection is future scope-selector work
 *  (CSW-1642) — until then "first active" is deterministic enough. A
 *  pending/denied staff member has no active membership ⇒ undefined ⇒ the
 *  handler's existing "complete setup first" 403, same as a missing legacy
 *  claim gave. */
export function claimActiveStaffOrgId(claims: CapicheClaims): string | undefined {
  if (claims.overflow) return undefined;
  const hit = Object.entries(claims.memberships).find(
    ([, m]) => (m.type === 'teacher' || m.type === 'staff') && m.status === 'active',
  );
  return hit?.[0];
}

/** The orgId of this user's first student membership, or undefined. Replaces
 *  the legacy `orgId` claim for student-scoped handlers (`GET /auth/me`). */
export function claimStudentOrgId(claims: CapicheClaims): string | undefined {
  if (claims.overflow) return undefined;
  const hit = Object.entries(claims.memberships).find(([, m]) => m.type === 'student');
  return hit?.[0];
}

/** True if the token carries the given Capiche-level platform role. The
 *  `role` argument is compile-checked against PLATFORM_ROLES, so a typo
 *  won't build. Replaces reading the legacy `capicheAdmin` claim. */
export function claimHasPlatformRole(
  claims: Pick<CapicheClaims, 'platform'>,
  role: PlatformRole,
): boolean {
  return claims.platform?.roles.includes(role) ?? false;
}

/** True if the token carries the given Capiche-level feature flag. `feature`
 *  is compile-checked against PLATFORM_FEATURES. Replaces reading the legacy
 *  `extensionBeta` claim. */
export function claimHasPlatformFeature(
  claims: Pick<CapicheClaims, 'platform'>,
  feature: PlatformFeature,
): boolean {
  return claims.platform?.features.includes(feature) ?? false;
}
